'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { usePathname } from 'next/navigation';
import { visaModell } from '@/lib/fordonsnamn';
import { Link } from '@/components/LocaleLink';
import { Car, ChevronLeft, ChevronRight } from 'lucide-react';
import { popularCategories } from '@/data/brands-popular';
import { useCartStore } from '@/stores/cart';
import { useLocale } from '@/hooks/useLocale';
import { translateCategory } from '@/i18n/categories';
import { iconForCategory } from '@/lib/category-icons';
import { t } from '@/i18n/t';

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3001/api';

interface VehicleCategory {
  id: number;
  name: string;
  slug: string;
  product_count: number;
}

export function CategoryNav() {
  const { locale } = useLocale();
  const pathname = usePathname() || '/';
  const selectedVehicle = useCartStore((state) => state.selectedVehicle);
  const ktype = selectedVehicle?.ktype;
  const [vehicleCategories, setVehicleCategories] = useState<VehicleCategory[]>([]);
  const [mounted, setMounted] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const scrollRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!ktype) {
      setVehicleCategories([]);
      return;
    }

    fetch(`${API_URL}/categories/vehicle-summary?ktype=${ktype}`)
      .then((res) => (res.ok ? res.json() : []))
      .then((data: VehicleCategory[]) => {
        setVehicleCategories(
          data
            .filter((c) => c.product_count > 0)
            .sort((a, b) => b.product_count - a.product_count)
            .slice(0, 12)
        );
      })
      .catch(() => setVehicleCategories([]));
  }, [ktype]);

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 2);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    checkScroll();
    el.addEventListener('scroll', checkScroll, { passive: true });
    const ro = new ResizeObserver(checkScroll);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', checkScroll);
      ro.disconnect();
    };
  }, [checkScroll, vehicleCategories, mounted]);

  const scroll = (direction: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    const amount = el.clientWidth * 0.6;
    el.scrollBy({ left: direction === 'left' ? -amount : amount, behavior: 'smooth' });
  };

  const showVehicleNav = mounted && ktype && vehicleCategories.length > 0;

  /**
   * Kategoriraden visas nu ÄVEN på mobil — utom i köpflödet.
   *
   * Den var `hidden lg:block`, alltså bara dator. Mobilkunden hade ingen väg in i
   * sortimentet alls från en produkt- eller söksida: bara hamburgaren och
   * "Katalog" i bottennavigeringen, som båda kräver att man vet att de finns.
   * Ägaren 2026-09-22: "gör det möjligt för kunder användarvänligt hitta det dem
   * ska, just nu tappar vi försäljning".
   *
   * Utesluten i varukorg, kassa och konto. Där ska kunden slutföra det hon
   * påbörjat, och raden hade bara konkurrerat med det — samma skäl som att
   * bottennavigeringen är dold på /kassa. Kostnaden är höjd över vecket, och det
   * var just den vi nyss betalade tillbaka i varukorgen.
   */
  const iKopfloden = /^\/(varukorg|kassa|konto|login|foretag)/.test(pathname)
    || /^\/en\/(varukorg|kassa|konto|login|foretag)/.test(pathname);
  if (iKopfloden) return <nav className="hidden lg:block border-b bg-background" aria-hidden />;

  return (
    <nav className="border-b bg-background">
      <div className="container mx-auto px-4 relative">
        {/* Left scroll arrow */}
        {canScrollLeft && (
          <button
            onClick={() => scroll('left')}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center w-8 h-8 rounded-full bg-background/95 border shadow-sm hover:bg-secondary transition-colors"
            aria-label={t(locale, 'nav.scrollLeft')}
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
        )}

        {/* Right scroll arrow */}
        {canScrollRight && (
          <button
            onClick={() => scroll('right')}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center w-8 h-8 rounded-full bg-background/95 border shadow-sm hover:bg-secondary transition-colors"
            aria-label={t(locale, 'nav.scrollRight')}
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        )}

        {/* Fade edges when scrollable */}
        {canScrollLeft && (
          <div className="absolute left-8 top-0 bottom-0 w-6 bg-gradient-to-r from-background to-transparent z-[5] pointer-events-none" />
        )}
        {canScrollRight && (
          <div className="absolute right-8 top-0 bottom-0 w-6 bg-gradient-to-l from-background to-transparent z-[5] pointer-events-none" />
        )}

        <ul
          ref={scrollRef}
          className="flex items-center gap-1 py-1 text-sm overflow-x-auto scrollbar-hide"
        >
          {showVehicleNav ? (
            <>
              <li className="flex items-center gap-1.5 px-2 py-2 text-sm text-primary font-semibold whitespace-nowrap">
                <Car className="h-4 w-4" />
                {selectedVehicle!.manufacturer} {visaModell(selectedVehicle!.model)}:
              </li>
              {vehicleCategories.map((cat) => {
                const Ikon = iconForCategory(cat.slug);
                return (
                  <li key={cat.id}>
                    <Link
                      href={`/fordon/${ktype}?category=${cat.id}`}
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-[15px] text-foreground/80 hover:text-foreground hover:bg-secondary transition-colors whitespace-nowrap"
                    >
                      <Ikon className="h-5 w-5 text-primary shrink-0" />
                      {translateCategory(cat.slug, cat.name, locale)}
                      <span className="text-xs text-muted-foreground/70">
                        ({cat.product_count})
                      </span>
                    </Link>
                  </li>
                );
              })}
            </>
          ) : (
            popularCategories.map((cat) => {
              const Ikon = iconForCategory(cat.slug);
              return (
                <li key={cat.slug}>
                  <Link
                    href={`/katalog/${cat.slug}`}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-[15px] text-foreground/80 hover:text-foreground hover:bg-secondary transition-colors whitespace-nowrap"
                  >
                    <Ikon className="h-5 w-5 text-primary shrink-0" />
                    {translateCategory(cat.slug, cat.name, locale)}
                  </Link>
                </li>
              );
            })
          )}
          <li>
            <Link
              href={ktype ? `/fordon/${ktype}` : '/katalog'}
              className="inline-flex items-center px-3 py-2 rounded-md text-[15px] text-primary font-medium hover:bg-primary/5 transition-colors whitespace-nowrap"
            >
              {t(locale, 'nav.allCategories')}
            </Link>
          </li>
        </ul>
      </div>
    </nav>
  );
}
